import type { Store } from '@blocksuite/affine/store';
import { ZipTransformer } from '@blocksuite/affine/widgets/linked-doc';

import type { WorkspaceService } from '../../workspace';
import { getStreamLainWorkspaceSchema } from '../../workspace/global-schema';
import {
  type ExportScopePlan,
  prepareDatabaseSubdocsForSnapshotExport,
  type SnapshotExportMode,
} from './database-subdoc-preload';

export type SnapshotExportScopeResolution = {
  plan: ExportScopePlan;
  stores: Store[];
};

export type ResolveSnapshotExportScopeInput = {
  docs: readonly Store[];
  workspaceService: WorkspaceService;
  mode: SnapshotExportMode;
};

export async function resolveSnapshotExportScope(
  input: ResolveSnapshotExportScopeInput
): Promise<SnapshotExportScopeResolution> {
  const plan = await prepareDatabaseSubdocsForSnapshotExport(
    input.docs,
    input.workspaceService,
    { mode: input.mode }
  );
  return {
    plan,
    stores: resolveStoresForExportScopePlan(input.docs, plan),
  };
}

export async function exportDocsWithSnapshotExportScope(input: {
  collection: Store['workspace'];
  docs: readonly Store[];
  workspaceService: WorkspaceService;
  mode: SnapshotExportMode;
  excludeBlobIds?: Set<string>;
  resolvedScope?: SnapshotExportScopeResolution;
}): Promise<ExportScopePlan> {
  const resolvedScope =
    input.resolvedScope ??
    (await resolveSnapshotExportScope({
      docs: input.docs,
      workspaceService: input.workspaceService,
      mode: input.mode,
    }));

  await ZipTransformer.exportDocs(
    input.collection,
    getStreamLainWorkspaceSchema(),
    resolvedScope.stores,
    input.excludeBlobIds
  );

  return resolvedScope.plan;
}

function resolveStoresForExportScopePlan(
  sourceStores: readonly Store[],
  plan: ExportScopePlan
): Store[] {
  const workspace = sourceStores[0]?.workspace;
  const byId = new Map<string, Store>();
  for (const store of sourceStores) {
    byId.set(store.id, store);
  }
  if (!workspace) {
    return [...byId.values()];
  }

  for (const docId of plan.requiredDocIds) {
    if (byId.has(docId)) {
      continue;
    }
    const doc = workspace.getDoc(docId);
    const store = doc?.getStore({ id: docId });
    if (store) {
      byId.set(docId, store);
    }
  }

  return [...byId.values()];
}
